import React, { useContext, useState, useEffect } from "react";
import { useMediaQuery } from "react-responsive";
import { LeaderboardContext } from "../../provider/leaderboard";
import truncateWallet from "../../utils/truncateWallet";
import "./index.scss";
import { ReactComponent as AllowedIcon } from "../../assets/media/icons/Allowed.svg";

import goldMedal from "../../assets/media/icons/goldMedal.png";
import finalLogoStar from "../../assets/media/icons/FinalLogoBig-star.png";
import ethLogoMedium from "../../assets/media/icons/ETH-Logo-Medium.png";
import singularStarGold from "../../assets/media/SingularStarGold.png";
import { useAccount } from "wagmi";
import { searchUsers } from "../../provider/leaderboardservice";
import Loader from "../Loader";
import { useDebounce } from "../../hooks/useDebounce";
import {
  computeTopPercentFromRank,
  getLeaderboardTotalParticipants,
  isBelowFiftyPercentPlacement,
} from "../../utils/resolveTopPercent";

/*
const getAllowlistTier = (record) => {
  if (!record) return null;
  return record.tier ?? record.owner_id?.tier ?? record.owner_id?.Tier ?? null;
};

const formatTier = (tier) => {
  if (tier == null) return "-";
  const str = String(tier).trim();
  if (!str) return "-";
  const withSpaces = str.replace(/_/g, " ");
  return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
};
*/

const formatLbNumber = (raw) => {
  if (raw == null || raw === "") return "0";
  const n = parseFloat(raw);
  if (Number.isNaN(n)) return "0";
  if (Math.abs(n) < 1 && n !== 0) return n.toFixed(2);
  return Math.round(n).toLocaleString();
};

const formatNftPnl = (el) => {
  const raw = el?.totalRevenue;
  if (raw == null) return "0";
  const n = parseFloat(raw);
  if (Number.isNaN(n)) return "0";
  if (n === 0 || parseFloat(n.toFixed(2)) === -0) return "0";
  return formatLbNumber(n);
};

const formatTradingPnlAmount = (el) => {
  const raw = el?.totalRevenue;
  if (raw == null) return "0";
  const n = parseFloat(raw);
  if (Number.isNaN(n)) return "0";
  if (n === 0 || parseFloat(n.toFixed(2)) === -0) return "0";
  return Math.round(n).toLocaleString();
};

const formatTradingPnlRoi = (el) => {
  const roiValue = el?.roi ?? null;
  const roiFormatted =
    roiValue != null ? parseFloat(roiValue).toFixed(1) : "0.0";
  return roiFormatted === "-0.0" ? "0.0" : roiFormatted;
};

/** ROI pill label: +18.4% or -2.6% */
const formatTradingPnlRoiParen = (el) => {
  const roiFormatted = formatTradingPnlRoi(el);
  const n = parseFloat(roiFormatted);
  if (Number.isNaN(n)) return "0.0%";
  const sign = n > 0 ? "+" : "";
  return `${sign}${roiFormatted}%`;
};

const formatLbDollar = (value) => {
  const str = String(value);
  if (str.startsWith("-")) return `-$${str.slice(1)}`;
  return `${str}`;
};

const formatPlacement = (el, leaderboardResult) => {
  const rawPct = el?.percentage;
  if (rawPct != null && rawPct !== "") {
    if (isBelowFiftyPercentPlacement(rawPct)) {
      return "-";
    }
    const n = Number(rawPct);
    if (Number.isFinite(n) && n > 0) {
      return `TOP ${Math.min(100, Math.round(n))}%`;
    }
    if (typeof rawPct === "string" && rawPct.trim()) {
      return rawPct.trim();
    }
  }

  const rank = Number(el?.rank);
  const total = getLeaderboardTotalParticipants(leaderboardResult);
  const computed = computeTopPercentFromRank(rank, total);
  if (computed != null) {
    return `TOP ${computed}%`;
  }

  return Number.isFinite(rank) && rank > 0 ? `#${rank}` : "—";
};

const LbEthValue = ({ value }) => (
  <span className="lb-stat-value lb-stat-value--eth">
    <span>{formatLbDollar(value)}</span>
    <img src={ethLogoMedium} alt="" className="lb-stat-value__icon eth-logo-star" aria-hidden="true" />
  </span>
);

/** Mobile Trading PNL: ETH icon before amount (no $ suffix). */
const LbTradingPnlMobileValue = ({ value }) => (
  <span className="lb-stat-value lb-stat-value--eth-leading">
    <img
      src={ethLogoMedium}
      alt=""
      className="lb-stat-value__icon lb-stat-value__icon--eth-leading"
      aria-hidden="true"
    />
    <span>{value}</span>
  </span>
);

const ThisWeekResult = ({ weektype, result, title, loading, winner }) => {
  const { address } = useAccount();
  const { searchInput } = useContext(LeaderboardContext);

  const [users, setUsers] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const debouncedSearchInput = useDebounce(searchInput?.trim(), 500);

  const isTabletDevice = useMediaQuery({ query: "(max-width: 889px)" });
  const isSmallDevice = useMediaQuery({ query: "(max-width: 480px)" });

  useEffect(() => {
    if (!searchInput && Array.isArray(result?.user_list)) {
      setUsers(result.user_list);
    }
  }, [result, searchInput]);

  useEffect(() => {
    if (!debouncedSearchInput) {
      if (result?.user_list) {
        setUsers(result.user_list);
      }
      setSearchLoading(false);
      return;
    }

    const performSearch = async () => {
      try {
        setSearchLoading(true);
        const response = await searchUsers(debouncedSearchInput, 10);

        if (response.data?.status && response.data?.users) {
          setUsers(response.data.users);
        } else {
          setUsers([]);
        }
      } catch (error) {
        console.error("Error searching users:", error);
        setUsers([]);
      } finally {
        setSearchLoading(false);
      }
    };

    performSearch();
  }, [debouncedSearchInput, result]);

  return (
    <>
      {loading ? (
        <div className="no-result">
          <Loader imageSrc={finalLogoStar} alt="Loading leaderboard" />
        </div>
      ) : searchLoading ? (
        <div
          className="no-result"
          style={{
            padding: "2rem",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "200px",
          }}
        >
          <img
            src={finalLogoStar}
            alt="Loading search results"
            style={{ width: "50px", height: "50px", objectFit: "contain" }}
          />
        </div>
      ) : users?.length > 0 ? (
        users?.map((el, index) => {
          const userrank = el.rank;
          const isCurrentUser =
            el?.walletAddress?.toLowerCase() === address?.toLowerCase();

          const handleName = String(el?.twitter_username ?? "").replace(/^@+/, "");
          const totalVolume = formatLbNumber(el?.totalVolume);
          const nftPnl = formatNftPnl(el);
          const placement = formatPlacement(el, result);
          const prizePoints = formatLbNumber(el?.PrizePoints ?? 0);
          const tradingPnlAmount = formatTradingPnlAmount(el);
          const tradingPnlRoi = formatTradingPnlRoi(el);
          const tradingPnlRoiParen = formatTradingPnlRoiParen(el);
          const roiColorClass =
            parseFloat(tradingPnlRoi) > 0
              ? "green"
              : parseFloat(tradingPnlRoi) < 0
                ? "red"
                : "white";
          const displayName =
            isTabletDevice && handleName.length > 30
              ? truncateWallet(handleName)
              : handleName;

          return (
            <div
              key={el?.walletAddress || index}
              onClick={() =>
                handleName && window.open(`https://x.com/${handleName}`, "_blank")
              }
              className={`weekly-canditate-wrapper non-winner ${
                isCurrentUser ? "currentuser" : ""
              }`}
              style={{ cursor: handleName ? "pointer" : "default" }}
            >
              <div className="lb-col-rank">
                <div className="weekly-rank">
                  <div className="weekly-rank-number">#{userrank}</div>
                </div>
              </div>

              <div className="lb-col-user">
                <div className="weekly-candidate-username">
                  <div className="top-wallet-address">
                    {el?.userAvatar && (
                      <img src={el.userAvatar} alt="" />
                    )}
                    <div className="weekly-candidate-username__text">
                      <span>@{displayName}</span>
                    </div>
                  </div>
                </div>
              </div>

              {!isSmallDevice && (
                <>
                  <div className="lb-col-volume">
                    <LbEthValue value={totalVolume} />
                  </div>
                  <div className="lb-col-nft-pnl weekly-candidate-lostgainedamount">
                    <LbEthValue value={nftPnl} />
                    <span className={`lb-nft-pnl-roi ${roiColorClass}`}>
                      {tradingPnlRoiParen}
                    </span>
                  </div>
                  <div className="lb-col-placement">
                    <span className="lb-stat-value">{placement}</span>
                  </div>
                  <div className="lb-col-prize">
                    <span className="lb-stat-value lb-stat-value--prize">
                      <span>{prizePoints}</span>
                      <img
                        src={singularStarGold}
                        alt=""
                        className="lb-stat-value__icon"
                        aria-hidden="true"
                      />
                    </span>
                  </div>
                </>
              )}

              {isSmallDevice && (
                <div className="lb-col-nft-pnl weekly-candidate-lostgainedamount">
                  <LbTradingPnlMobileValue value={tradingPnlAmount} />
                  <span className={`lb-nft-pnl-roi ${roiColorClass}`}>
                    {tradingPnlRoiParen}
                  </span>
                </div>
              )}

              {/* Tier column — hidden for now
              {!isSmallDevice && (
                <div className="lb-col-tier">{formatTier(getAllowlistTier(el))}</div>
              )}
              */}
            </div>
          );
        })
      ) : (
        <div className="no-result">
          <p>No results...</p>
        </div>
      )}

    </>
  );
};

export default ThisWeekResult;
